import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Play, Brain, Hash, Vote, HandHeart } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useSignupDialog } from "@/contexts/SignupDialogContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSetting } from "@/hooks/useSiteSettings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import QuickGiveDialog from "@/components/giving/QuickGiveDialog";

const JoinQuizButton = () => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="hero-outline" size="lg" className="px-6 py-5 md:px-8 md:py-6 text-base md:text-lg border-primary/60 text-primary hover:bg-primary/5">
          <Brain className="mr-2 h-5 w-5" /> Join Quiz Game
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Hash className="w-5 h-5 text-primary" /> Enter Game Code</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <Input placeholder="Enter 6-digit code" value={code} onChange={e => setCode(e.target.value)} className="text-center text-xl font-mono tracking-[0.3em]" maxLength={6} />
          <Button className="w-full" disabled={code.length < 6} onClick={() => { setOpen(false); navigate(`/quizverse?tab=kahoot&code=${code}`); }}>
            <Play className="w-4 h-4 mr-2" /> Join Game
          </Button>
          <p className="text-xs text-center text-muted-foreground">Don't have a code? <Link to="/quizverse?tab=kahoot" className="text-primary hover:underline" onClick={() => setOpen(false)}>Create or browse games</Link></p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuickGiveButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="hero-outline"
        size="lg"
        className="px-6 py-5 md:px-8 md:py-6 text-base md:text-lg border-primary/60 text-primary hover:bg-primary/5"
        onClick={() => setOpen(true)}
      >
        <HandHeart className="mr-2 h-5 w-5" /> Quick Give
      </Button>
      <QuickGiveDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

const HeroSection = () => {
  const { openSignupDialog } = useSignupDialog();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: registrationDisabled } = useSiteSetting("registration_disabled");
  const handleGetStarted = () => (user ? navigate("/feed") : openSignupDialog());
  return (
    <section className="relative min-h-[40dvh] md:min-h-[70dvh] flex items-start md:items-center justify-center overflow-hidden pt-0 md:pt-0">
      {/* Static background (animated blurs removed for perf — caused mobile scroll jank) */}
      <div className="absolute inset-0 bg-background" />
      <div className="hidden md:block absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />

      <div className="container relative z-10 px-4 py-2 md:py-20 text-center mt-0 md:mt-0">
        <div>
        <motion.h1
          className="font-display text-[28px] sm:text-[32px] md:text-7xl lg:text-8xl font-bold tracking-tight mb-2 md:mb-6 leading-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <span className="block">One Platform.</span>
          <span className="gradient-text">Infinite Possibilities.</span>
        </motion.h1>

        <motion.p
          className="max-w-2xl mx-auto text-base md:text-xl text-muted-foreground mb-4 md:mb-10 px-2"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          Social media, marketplace, events, freelancing, dating, payments, and AI tools — all unified in one powerful ecosystem.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-3 justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Button
            variant="hero"
            size="lg"
            className="px-6 py-5 md:px-8 md:py-6 text-base md:text-lg"
            onClick={handleGetStarted}
            disabled={!user && registrationDisabled}
            title={!user && registrationDisabled ? "Registration is temporarily disabled" : undefined}
          >
            {user ? "Go to Home" : registrationDisabled ? "Registration disabled" : "Get Started Free"} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button variant="hero-outline" size="lg" className="px-6 py-5 md:px-8 md:py-6 text-base md:text-lg" asChild>
            <Link to="/elections/upload"><Vote className="mr-2 h-5 w-5" /> Submit Election Result</Link>
          </Button>
          <JoinQuizButton />
          <QuickGiveButton />
        </motion.div>

        <motion.div
          className="mt-2 md:mt-16 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-muted-foreground text-sm md:text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500" /> 10M+ Users</span>
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary" /> 70+ Features</span>
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-accent" /> 150+ Countries</span>
        </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
